<template>
  <!-- link chip -->
  <template v-if="messageItem.type === 'link'">
    <div class="p-chat-item p-chat-item__link"><div v-text="messageItem.message" /></div>
  </template>
  <!-- question chip -->
  <template v-if="messageItem.type === 'question'">
    <div class="p-chat-item p-chat-item__question">
      <div v-text="messageItem.message" />
      <p-icon icon="caret-right" size="xmd" color="fg-primary" />
    </div>
  </template>
  <!-- open-form chip -->
  <template v-if="messageItem.type === 'open-form'">
    <div class="p-chat-item p-chat-item__open-form">
      <nuxt-link :to="messageItem.message" target="_blank">
        <div v-text="messageItem.message" />
      </nuxt-link>
      <p-icon icon="arrow-top-right" size="xmd" color="fg-primary" />
    </div>
  </template>
  <!-- file chip -->
  <template v-else-if="messageItem.type === 'file'">
    <div class="p-chat-item p-chat-item__file"><div v-text="messageItem.message" /></div>
  </template>
  <!-- outbound bubble (sender = current user) -->
  <template v-else-if="messageItem.type === 'sender'">
    <div class="p-chat-item p-chat-item--outbound">
      <div class="p-chat-item__bubble p-chat-item__bubble--outbound">
        <p-chat-message-content
          :attachments="messageItem.attachments"
          :message="messageItem.message"
          :time="messageItem.time"
          :edited="messageItem.edited"
          :get-attachment-view-url="getAttachmentViewUrl"
          :is-attachment-loading="isAttachmentLoading"
          :handle-download-click="handleDownloadClick"
          :is-image="isImage"
          :format-file-size="formatFileSize"
          :get-file-extension="getFileExtension"
        />
      </div>
    </div>
  </template>
  <!-- inbound bubble (receiver = other party) -->
  <template v-else-if="messageItem.type === 'receiver'">
    <div class="p-chat-item p-chat-item--inbound">
      <div class="p-chat-item__bubble p-chat-item__bubble--inbound">
        <p-chat-message-content
          :attachments="messageItem.attachments"
          :message="messageItem.message"
          :time="messageItem.time"
          :edited="messageItem.edited"
          :get-attachment-view-url="getAttachmentViewUrl"
          :is-attachment-loading="isAttachmentLoading"
          :handle-download-click="handleDownloadClick"
          :is-image="isImage"
          :format-file-size="formatFileSize"
          :get-file-extension="getFileExtension"
        />
      </div>
    </div>
  </template>
  <!-- AI bot — no bubble, markdown rendered (bold/italic/lists/links) -->
  <template v-else-if="messageItem.type === 'bot'">
    <div class="p-chat-item p-chat-item--bot">
      <div class="p-chat-item__bot-content" v-html="botMarkdown" />
      <p-chat-timestamp
        :time="messageItem.time"
        :edited="messageItem.edited"
        class="p-chat-item__bot-time f-utility-12 t-fg-tertiary"
      />
    </div>
  </template>
  <!-- typing indicator -->
  <template v-else-if="messageItem.type === 'typing'">
    <div class="p-chat-item p-chat-item--inbound">
      <div class="p-chat-item__bubble p-chat-item__bubble--inbound p-chat-item__bubble--typing">
        <span class="p-chat-item__dot" />
        <span class="p-chat-item__dot" />
        <span class="p-chat-item__dot" />
      </div>
    </div>
  </template>
</template>
<script setup lang="ts">
import { marked, Renderer } from 'marked'
import { useHelpdeskChatStore } from '@/store/helpdeskChat'
import { useSnackbarStore } from '@/store/snackbar'

interface Attachment {
  id: string | number
  type: 'image' | 'file'
  uri?: string
  fileName: string
  mimeType: string
  sizeBytes?: number
}

interface MessageItem {
  message: string
  time?: string
  // Set when an agent has edited this message (helpdesk message edit/delete).
  edited?: boolean
  type: 'link' | 'file' | 'sender' | 'receiver' | 'question' | 'open-form' | 'typing' | 'bot'
  attachments?: Attachment[]
}

const props = defineProps<{ messageItem: MessageItem }>()

// Restricted markdown renderer for bot messages.
// Whitelist: bold, italic, lists, links, paragraphs, line breaks.
// Everything else (headings, blockquote, hr, code, table, image, raw HTML)
// falls back to plain text. Combined with the up-front `<` / `>` escape
// below, this keeps v-html safe without a DOMPurify dependency.
const botRenderer = new Renderer()
const escapeText = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
botRenderer.heading = ({ tokens }) => marked.parser(tokens) + '<br>'
botRenderer.blockquote = ({ tokens }) => marked.parser(tokens)
botRenderer.hr = () => ''
botRenderer.code = ({ text }) => escapeText(text)
botRenderer.codespan = ({ text }) => escapeText(text)
botRenderer.table = () => ''
botRenderer.image = ({ text }) => escapeText(text)
botRenderer.html = () => ''
botRenderer.link = ({ href, tokens }) => {
  const text = marked.parseInline(tokens.map((t) => ('raw' in t ? t.raw : '')).join(''))
  if (!href || !/^(https?:|mailto:|tel:|\/|#)/i.test(href)) return String(text)
  const safeHref = href.replace(/"/g, '&quot;')
  return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${text}</a>`
}

const botMarkdown = computed(() => {
  if (props.messageItem.type !== 'bot') return ''
  // Escape any raw HTML in the source so the renderer above only emits
  // tags it produced itself — never pass-through `<script>`/etc.
  const escaped = escapeText(props.messageItem.message ?? '')
  return marked.parse(escaped, { breaks: true, gfm: true, renderer: botRenderer }) as string
})

const chat = useHelpdeskChatStore()
const snackbarStore = useSnackbarStore()
const attachmentViewUrls = ref<Record<string, string>>({})
const loadingAttachments = ref<Set<string>>(new Set())

const keyOf = (id: string | number) => String(id)

const isAttachmentLoading = (id: string | number) => loadingAttachments.value.has(keyOf(id))

const getAttachmentViewUrl = (id: string | number): string =>
  attachmentViewUrls.value[keyOf(id)] || ''

const isImage = (att: Attachment): boolean => att.type === 'image'

// Optimistic ids (`local-…`) live in the message store until upload resolves.
// They are not server-side resources, so any /view-url or /download-url call
// for them is guaranteed to 404 — and gorilla/mux's [0-9]+ id constraint
// surfaces that 404 with no CORS headers, which the browser reports as a
// misleading CORS error. Skip the fetch until the message reconciles.
const isOptimisticId = (id: string | number) => String(id).startsWith('local-')

const fetchAttachmentViewUrl = async (att: Attachment) => {
  const k = keyOf(att.id)
  if (att.uri) {
    attachmentViewUrls.value[k] = att.uri
    return
  }
  if (isOptimisticId(att.id)) return
  loadingAttachments.value.add(k)
  try {
    const url = await chat.getAttachmentViewUrl(String(att.id))
    if (url) attachmentViewUrls.value[k] = url
  } finally {
    loadingAttachments.value.delete(k)
  }
}

// Save a local URL with a synthetic anchor click rather than window.open.
// The URL is only known after an await, by which point the click's user
// activation has expired — Safari (and iOS Safari, where pop-up blocking
// is on by default) drops window.open silently, so the user taps download
// and nothing whatsoever happens. An anchor click is never
// pop-up-blocked, and because the href is same-origin (a blob: URL, or
// the local object URL of a not-yet-uploaded file) the `download`
// attribute applies and names the file exactly — Turkish characters
// included.
const triggerBrowserDownload = (url: string, fileName: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

const handleDownloadClick = async (att: Attachment) => {
  const k = keyOf(att.id)
  if (loadingAttachments.value.has(k)) return
  // Optimistic ids aren't server-side resources yet (see isOptimisticId), so
  // there is nothing to fetch — but the object URL the store minted for the
  // preview is right here and already same-origin.
  if (isOptimisticId(att.id)) {
    if (att.uri) triggerBrowserDownload(att.uri, att.fileName)
    return
  }
  loadingAttachments.value.add(k)
  try {
    // The whole file comes down here, so the spinner covers a real transfer
    // now rather than a URL lookup.
    const { blob, tokenExpired } = await chat.fetchAttachmentBlob(String(att.id))
    if (blob) {
      const objectUrl = URL.createObjectURL(blob)
      triggerBrowserDownload(objectUrl, att.fileName)
      // Revoking synchronously can cancel the save in Safari before it has
      // taken ownership of the blob, but every extra second keeps a second
      // copy of the file alive in the tab's heap — so this is the shortest
      // grace period that survives the handoff, not a comfortable one.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2_000)
      return
    }
    // An expired session already sent the user to re-auth (or back to the
    // guest form); a second "download failed" toast on the way out would
    // only be noise.
    if (tokenExpired) return
    // Anything else — revoked access, a stalled transfer, a network blip.
    // Previously silent, which is indistinguishable from a dead button.
    snackbarStore.setSnackbar({
      severity: 'danger',
      title: { langkey: 'chat.fileError.downloadFailed', params: {} },
      timeout: 5000,
    })
  } finally {
    loadingAttachments.value.delete(k)
  }
}

watch(
  () => props.messageItem.attachments,
  (atts) => {
    if (!atts || atts.length === 0) return
    // Seed the local view-url cache for images so they render inline.
    // Files defer URL resolution until the user taps download — matches
    // samaritan and avoids a redundant pre-fetch on every render.
    atts.forEach((a) => {
      if (a.type === 'image') fetchAttachmentViewUrl(a)
    })
  },
  { immediate: true, deep: true },
)

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const getFileExtension = (fileName: string): string => {
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : ''
}
</script>
